import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, Checkbox, Loading, ErrorNote, PageHeader,
    confirmAction, toast
} from '../components/ui';
import { DataTable } from '../components/DataTable';

interface Payload {
    apiKeys: {
        id: number; name: string; keyPrefix: string; scopes: string[];
        expiresAt: string | null; lastUsedAt: string | null; isActive: boolean;
    }[];
    webhooks: {
        id: number; name: string; url: string; secretRef: string | null; isSigned: boolean;
        events: string[]; isActive: boolean; pendingCount: number; abandonedCount: number;
    }[];
    recentDeliveries: {
        id: number; webhookName: string; eventType: string; statusCode: number | null;
        attemptCount: number; lastError: string | null; deliveredAt: string | null;
        nextRetryAt: string | null; abandoned: boolean; createdAt: string;
    }[];
    allScopes: string[];
    allEvents: string[];
}

const SCOPE_LABELS: Record<string, string> = {
    'candidates:write': 'Add and update candidates',
    'candidates:read': 'Read candidates',
    'interviews:write': 'Create interviews',
    'interviews:read': 'Read interview results',
    'templates:read': 'List templates'
};

const EVENT_LABELS: Record<string, string> = {
    'interview.created': 'An interview was created',
    'interview.completed': 'A candidate finished an interview',
    'interview.reviewed': 'An interview was approved or rejected'
};

const EMPTY_HOOK = {
    id: undefined as number | undefined,
    name: '', url: '', secretRef: '', events: [] as string[]
};

export function Integrations() {
    const queryClient = useQueryClient();
    const [keyName, setKeyName] = useState('');
    const [keyScopes, setKeyScopes] = useState<string[]>([]);
    const [expiresInDays, setExpiresInDays] = useState('');
    const [newKey, setNewKey] = useState<string | null>(null);
    const [hook, setHook] = useState(EMPTY_HOOK);
    const [error, setError] = useState<ApiError | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['integrations'],
        queryFn: () => api.get<Payload>('/api/admin/integrations')
    });

    const refresh = () => queryClient.invalidateQueries({ queryKey: ['integrations'] });

    const createKey = useMutation({
        mutationFn: () => api.post<{ key: string }>('/api/admin/integrations/api-keys', {
            name: keyName,
            scopes: keyScopes,
            expiresInDays: expiresInDays ? Number(expiresInDays) : null
        }),
        onSuccess: async (result) => {
            setNewKey(result.key);
            setKeyName('');
            setKeyScopes([]);
            setExpiresInDays('');
            setError(null);
            await refresh();
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const revokeKey = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/integrations/api-keys/${id}/revoke`),
        onSuccess: async () => { toast('Key revoked.'); await refresh(); },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const saveHook = useMutation({
        mutationFn: () => api.post('/api/admin/integrations/webhooks', hook),
        onSuccess: async () => {
            toast('Webhook saved.');
            setHook(EMPTY_HOOK);
            setError(null);
            await refresh();
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const disableHook = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/integrations/webhooks/${id}/disable`),
        onSuccess: async () => { toast('Webhook disabled.'); await refresh(); },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const retry = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/integrations/deliveries/${id}/retry`),
        onSuccess: async () => { toast('Delivered.'); await refresh(); },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    if (isLoading || !data) return <Loading />;

    const toggle = (list: string[], value: string) =>
        list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

    return (
        <div className="space-y-6">
            {/* API keys and webhooks are peer sections, so neither card's title can be the page's. */}
            <PageHeader
                title="Other systems"
                lede="How a customer's software talks to this one: keys for calling in, webhooks for being told when something happened."
            />

            {newKey && (
                <Card title="Your new API key">
                    <p className="mb-2 text-sm text-slate-600">
                        Copy it now. Only a one-way hash is stored, so it cannot be shown again — a
                        lost key has to be revoked and replaced.
                    </p>
                    <div className="flex gap-2">
                        <TextInput readOnly value={newKey} onFocus={(e) => e.target.select()} />
                        <Button
                            variant="secondary"
                            onClick={async () => {
                                await navigator.clipboard.writeText(newKey);
                                toast('Copied.');
                            }}
                        >
                            Copy
                        </Button>
                        <Button variant="secondary" onClick={() => setNewKey(null)}>Done</Button>
                    </div>
                </Card>
            )}

            <Card title="API keys">
                <p className="mb-4 text-sm text-slate-600">
                    For a customer system calling <code className="text-xs">api/v1</code>. Separate from
                    user accounts, so revoking an integration never signs a person out.
                </p>

                <DataTable
                    rows={data.apiKeys}
                    rowKey={(row) => row.id}
                    empty="No keys issued yet."
                    columns={[
                        { header: 'Name', sortBy: (row) => row.name, cell: (row) => <span className="font-medium">{row.name}</span> },
                        { header: 'Starts with', cell: (row) => <code className="text-xs">{row.keyPrefix}…</code> },
                        {
                            header: 'Can do',
                            cell: (row) => row.scopes.map((s) => SCOPE_LABELS[s] ?? s).join(', ')
                        },
                        {
                            header: 'Last used',
                            cell: (row) => row.lastUsedAt
                                ? new Date(row.lastUsedAt).toLocaleString()
                                // A key that has never been used is usually a broken integration.
                                : <span className="text-xs text-amber-700">never</span>
                        },
                        {
                            header: 'Expires',
                            cell: (row) => row.expiresAt
                                ? new Date(row.expiresAt).toLocaleDateString()
                                : 'never'
                        },
                        {
                            header: '',
                            className: 'text-right',
                            cell: (row) => row.isActive ? (
                                <Button
                                    variant="secondary"
                                    onClick={async () => {
                                        const confirmed = await confirmAction({
                                            title: `Revoke ${row.name}?`,
                                            text: 'Any system using this key stops working immediately.',
                                            confirmText: 'Revoke key',
                                            danger: true
                                        });
                                        if (confirmed) revokeKey.mutate(row.id);
                                    }}
                                >
                                    Revoke
                                </Button>
                            ) : <span className="text-xs text-slate-500">revoked</span>
                        }
                    ]}
                />

                <form
                    className="mt-6 max-w-lg space-y-4 border-t border-slate-100 pt-5"
                    onSubmit={(e) => { e.preventDefault(); createKey.mutate(); }}
                >
                    <h3 className="text-sm font-semibold text-slate-700">Issue a new key</h3>

                    {error && !error.errors && <ErrorNote message={error.message} />}

                    <Field label="What is it for?" error={error?.fieldError('name')}>
                        <TextInput
                            value={keyName}
                            onChange={(e) => setKeyName(e.target.value)}
                            placeholder="e.g. Voltas ATS"
                        />
                    </Field>

                    <Field label="What may it do?" error={error?.fieldError('scopes')}>
                        <div className="space-y-1.5">
                            {data.allScopes.map((scope) => (
                                <Checkbox
                                    key={scope}
                                    label={SCOPE_LABELS[scope] ?? scope}
                                    checked={keyScopes.includes(scope)}
                                    onChange={() => setKeyScopes((s) => toggle(s, scope))}
                                />
                            ))}
                        </div>
                    </Field>

                    <Field label="Expires after" hint="Days. Leave blank for a key that never expires.">
                        <TextInput
                            type="number"
                            min="1"
                            value={expiresInDays}
                            onChange={(e) => setExpiresInDays(e.target.value)}
                        />
                    </Field>

                    <Button
                        type="submit"
                        busy={createKey.isPending}
                        disabled={!keyName || keyScopes.length === 0}
                    >
                        Issue key
                    </Button>
                </form>
            </Card>

            <Card title="Webhooks">
                <p className="mb-4 text-sm text-slate-600">
                    How a customer's system learns an interview finished, without polling. Failed
                    deliveries are retried with a growing delay and then listed below rather than
                    dropped.
                </p>

                <DataTable
                    rows={data.webhooks}
                    rowKey={(row) => row.id}
                    empty="No webhooks yet."
                    columns={[
                        { header: 'Name', cell: (row) => <span className="font-medium">{row.name}</span> },
                        { header: 'Posts to', cell: (row) => <span className="break-all text-xs">{row.url}</span> },
                        {
                            header: 'Sends',
                            cell: (row) => row.events.length === 0
                                ? 'everything'
                                : row.events.map((e) => EVENT_LABELS[e] ?? e).join(', ')
                        },
                        {
                            header: 'Signed',
                            // An unsigned webhook cannot be verified by the receiver, which is worth
                            // seeing at a glance.
                            cell: (row) => row.isSigned
                                ? 'Yes'
                                : <span className="text-xs text-amber-700">No</span>
                        },
                        {
                            header: 'Waiting',
                            cell: (row) => (
                                <>
                                    {row.pendingCount}
                                    {row.abandonedCount > 0 && (
                                        <span className="ml-1 text-xs text-red-700">
                                            ({row.abandonedCount} given up on)
                                        </span>
                                    )}
                                </>
                            )
                        },
                        {
                            header: '',
                            className: 'text-right',
                            cell: (row) => (
                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="secondary"
                                        onClick={() => setHook({
                                            id: row.id,
                                            name: row.name,
                                            url: row.url,
                                            secretRef: row.secretRef ?? '',
                                            events: row.events
                                        })}
                                    >
                                        Edit
                                    </Button>
                                    {row.isActive && (
                                        <Button
                                            variant="secondary"
                                            onClick={async () => {
                                                const confirmed = await confirmAction({
                                                    title: `Disable ${row.name}?`,
                                                    confirmText: 'Disable webhook',
                                                    danger: true
                                                });
                                                if (confirmed) disableHook.mutate(row.id);
                                            }}
                                        >
                                            Disable
                                        </Button>
                                    )}
                                </div>
                            )
                        }
                    ]}
                />

                <form
                    className="mt-6 max-w-lg space-y-4 border-t border-slate-100 pt-5"
                    onSubmit={(e) => { e.preventDefault(); saveHook.mutate(); }}
                >
                    <h3 className="text-sm font-semibold text-slate-700">
                        {hook.id ? `Edit ${hook.name}` : 'Add a webhook'}
                    </h3>

                    <Field label="Name" error={error?.fieldError('name')}>
                        <TextInput
                            value={hook.name}
                            onChange={(e) => setHook((h) => ({ ...h, name: e.target.value }))}
                        />
                    </Field>

                    <Field label="Post to" error={error?.fieldError('url')}>
                        <TextInput
                            value={hook.url}
                            onChange={(e) => setHook((h) => ({ ...h, url: e.target.value }))}
                            placeholder="https://example.com/hooks/aiinterview"
                        />
                    </Field>

                    <Field
                        label="Signing secret"
                        hint="The name of an environment variable holding the shared secret — never the secret itself. Without one, the receiver cannot verify the payload came from you."
                    >
                        <TextInput
                            value={hook.secretRef}
                            onChange={(e) => setHook((h) => ({ ...h, secretRef: e.target.value }))}
                            placeholder="e.g. VOLTAS_WEBHOOK_SECRET"
                        />
                    </Field>

                    <Field label="Send which events?" hint="None ticked means every event.">
                        <div className="space-y-1.5">
                            {data.allEvents.map((event) => (
                                <Checkbox
                                    key={event}
                                    label={EVENT_LABELS[event] ?? event}
                                    checked={hook.events.includes(event)}
                                    onChange={() =>
                                        setHook((h) => ({ ...h, events: toggle(h.events, event) }))}
                                />
                            ))}
                        </div>
                    </Field>

                    <div className="flex gap-2">
                        <Button type="submit" busy={saveHook.isPending}>Save webhook</Button>
                        {hook.id && (
                            <Button type="button" variant="secondary" onClick={() => setHook(EMPTY_HOOK)}>
                                Cancel
                            </Button>
                        )}
                    </div>
                </form>
            </Card>

            <Card title="Recent deliveries">
                <DataTable
                    rows={data.recentDeliveries}
                    rowKey={(row) => row.id}
                    empty="Nothing has been sent yet."
                    columns={[
                        { header: 'When', cell: (row) => new Date(row.createdAt).toLocaleString() },
                        { header: 'Webhook', cell: (row) => row.webhookName },
                        {
                            header: 'Event',
                            cell: (row) => EVENT_LABELS[row.eventType] ?? row.eventType
                        },
                        {
                            header: 'Outcome',
                            cell: (row) => row.deliveredAt
                                ? <span className="text-xs text-emerald-700">Delivered</span>
                                : row.abandoned
                                    ? <span className="text-xs text-red-700">Given up after {row.attemptCount} tries</span>
                                    : <span className="text-xs text-amber-700">
                                        Retrying{row.nextRetryAt && ` at ${new Date(row.nextRetryAt).toLocaleTimeString()}`}
                                    </span>
                        },
                        {
                            header: 'Why not',
                            cell: (row) => row.lastError
                                ? <span className="text-xs">{row.lastError}</span>
                                : row.statusCode ?? '—'
                        },
                        {
                            header: '',
                            className: 'text-right',
                            cell: (row) => !row.deliveredAt && (
                                <Button
                                    variant="secondary"
                                    busy={retry.isPending && retry.variables === row.id}
                                    onClick={() => retry.mutate(row.id)}
                                >
                                    Try again
                                </Button>
                            )
                        }
                    ]}
                />
            </Card>
        </div>
    );
}
